import { useAuth } from "hooks/useAuth";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "store";
import { selectPendingVerification } from "store/slices/auth/auth.selector";

export default function OTPScreen() {
  const navigate = useNavigate();
  const [otp, setOtp] = useState<string[]>(Array(6).fill(""));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const pendingVerification = useAppSelector(selectPendingVerification);
  const { verifyOtp, resendOtp } = useAuth();
  const [resendCooldown, setResendCooldown] = useState(30);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [resendSuccess, setResendSuccess] = useState(false);
const [hasResent, setHasResent] = useState(false);

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    setOtpError(null);
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").slice(0, 6).split("");
    const newOtp = [...otp];
    pasted.forEach((char, i) => {
      if (/^\d$/.test(char)) newOtp[i] = char;
    });
    setOtp(newOtp);
    inputRefs.current[Math.min(pasted.length, 5)]?.focus();
  };

  const handleVerify = async () => {
    const otpValue = otp.join("");
    if (otpValue.length < 6) return;
    setOtpError(null);

    const success = await verifyOtp({
      email: pendingVerification?.email ?? "",
      otp: otpValue,
    });

    if (!success) {
      setOtpError("Invalid OTP. Please check your email and try again.");
      setOtp(Array(6).fill(""));
      inputRefs.current[0]?.focus();
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setResendSuccess(false);
setHasResent(true);
    await resendOtp(pendingVerification?.email ?? "");

    setResendSuccess(true);
    setResendCooldown(30);
    setOtpError(null);
    setOtp(Array(6).fill(""));
    inputRefs.current[0]?.focus();

    const timer = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setResendSuccess(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };
  useEffect(() => {
  const timer = setInterval(() => {
    setResendCooldown((prev) => {
      if (prev <= 1) {
        clearInterval(timer);
        return 0;
      }
      return prev - 1;
    });
  }, 1000);
  return () => clearInterval(timer);
}, []);

return (
  <div className="min-h-screen flex items-center justify-center bg-primary px-4 py-8">
    <div className="relative flex items-center justify-center bg-primary-intemediate rounded-2xl overflow-hidden w-full max-w-[1500px] min-h-[85vh]">
      
      <div className="bg-[#111827] rounded-2xl w-full max-w-[500px] p-6 md:p-10 mx-4">

        {/* Email icon */}
        <div className="flex justify-center mb-6">
          <div className="w-12 h-12 rounded-full bg-[#1E2230] border border-[#6366F1]/40 flex items-center justify-center">
            <svg className="w-5 h-5 text-[#6366F1]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="font-semibold text-xl text-white mb-2">
            Please check your email.
          </h1>
          <p className="text-sm text-neutral-medium">
            We've sent a code to{" "}
            <span className="text-[#6366F1] font-medium break-all">
              {pendingVerification?.email ?? "your email"}
            </span>
          </p>
        </div>

        {/* OTP Inputs */}
        <div className="flex gap-2 md:gap-3 justify-center mb-2" onPaste={handlePaste}>
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => { inputRefs.current[index] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              className={`w-10 h-10 md:w-12 md:h-12 text-center text-base md:text-lg font-semibold rounded-lg border bg-[#1E2230] text-white outline-none transition-all
                ${otpError ? "border-red-500" : digit ? "border-[#6366F1]" : "border-[#374151]"}
                focus:border-[#6366F1] focus:ring-1 focus:ring-[#6366F1]`}
            />
          ))}
        </div>

        {/* Inline error */}
        <div className="h-5 mb-3 text-center">
          {otpError && <p className="text-red-400 text-xs">{otpError}</p>}
        </div>

        {/* Resend */}
      {!hasResent && resendCooldown > 0 ? null : (
  <div className="text-center mb-8">
    <p className="text-sm text-gray-400">
      Didn't get a code?{" "}
      <button
        onClick={handleResend}
        disabled={resendCooldown > 0}
        className="text-[#6366F1] hover:underline disabled:text-gray-500 disabled:cursor-not-allowed"
      >
        {resendCooldown > 0 && hasResent ? `Resend in ${resendCooldown}s` : "Click to resend."}
      </button>
    </p>
  </div>
)}

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex-1 h-[46px] rounded-lg border border-[#374151] text-sm text-gray-300 hover:bg-[#1E2230] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleVerify}
            disabled={otp.join("").length < 6}
            className="flex-1 h-[46px] rounded-lg bg-[#6366F1] text-white text-sm font-medium hover:bg-[#4F46E5] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Verify
          </button>
        </div>

      </div>
    </div>
  </div>
);
}
